import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  DeleteForever,
  DriveFileRenameOutline,
  AddOutlined,
  DownloadSharp,
} from '@mui/icons-material';
import { addHours, startOfDay } from 'date-fns';
import { FileAPI } from '../../api/file-v2';
import { getFileLinkGroupOptions } from '../../api/options';
import {
  dateFormats,
  formatDateTime,
  fromUtcDateStringToDate,
  joinString,
} from '../../shared/utils';
import { useNotificationContext } from './notification';
import {
  colors,
  CompactTableCell,
  CompactTableRow,
  GridContainer,
  GridItem,
  PaddedCard,
} from './ui-extension';
import { LabeledInput } from './labeled-input';
import Modal from './modal';

const defaultLinkGroup = 'Other Files';

/**
 * Boolean flag the determines if the dialog should be shown.
 * @param {boolean} props.show
 * State setter for showing the dialog.
 * @param {Function} props.setShow
 * The default title of the dialog.
 * @param {string} props.defaultTitle
 * Defines the document link type or types for the dialog.
 * @param {string | string[]} props.documentLinkType
 * Get a list of document links
 * @param {Function} props.getDocumentLinks
 * Create or update document link
 * @param {Function} props.addDocumentLinks
 * Download/view a document link file
 * @param {Function} props.viewDocumentLinksFile
 * Defines a list of folders the user has access to. If the user has permission to modify all folders, keep the array empty.
 * @param {string[]} props.allowedLinkGroups
 * Defines a list of folders that required an expiration date.
 * @param {string[]} props.linkGroupsRequireExpiration
 * **true** if we don't want to show the areas.
 * @param {boolean} props.hideAreas
 * Defines a list of custom columns to display for each document link.
 * @param {Object[]} props.customColumns
 * When this object is provided, a new document link will not save automatically.
 * The user needs to provide extra data before saving the document link.
 * @param {Object} props.newFileFields
 * Defines how to get the file identifier for comparison.
 * @param {Function} props.getFileIdentifier
 **/
function DocumentLinksDialog({
  show = false,
  setShow = () => {},
  defaultTitle = '',
  documentLinkType,
  entityType,
  entityId,
  getDocumentLinks = async () => {},
  addDocumentLinks = async () => {},
  viewDocumentLinksFile = async () => {},
  allowedLinkGroups = [],
  linkGroupsRequireExpiration = [],
  hideAreas = false,
  customColumns = [],
  newFileFields,
  getFileIdentifier = (file) => file.row,
}) {
  const { useExceptionHandler, openDialog, showMessage } =
    useNotificationContext();
  const [documentLinks, setDocumentLinks] = useState([]);
  const [documentLinkSaving, setDocumentLinkSaving] = useState(false);
  const [linkGroupOptions, setLinkGroupOptions] = useState([]);
  const [selectedLinkGroup, setSelectedLinkGroup] = useState(defaultLinkGroup);
  const [fileIdBeingEdited, setFileIdBeingEdited] = useState(null);

  const updatedDocumentLinks = documentLinks.filter((doc) => doc.updated);

  const documentLinkGroups = documentLinks
    .map((dl) => dl.link_group)
    .filter((g) => !!g);

  const uniqueGroups = [
    ...new Set([
      ...linkGroupOptions.map((o) => o.value),
      ...documentLinkGroups,
    ]),
  ];

  const canAddNewLinkGroup = allowedLinkGroups.length === 0;

  const showExpirationDate =
    linkGroupsRequireExpiration.includes(selectedLinkGroup);

  useEffect(() => {
    (async () => {
      if (show) {
        loadLinkGroupOptions();
        const documentLinks = await loadDocumentLinks();
        setDefaultLinkGroup(documentLinks);
      }
    })();
  }, [show]);

  const loadDocumentLinks = useExceptionHandler(async () => {
    const documentLinks = await getDocumentLinks();
    setDocumentLinks(documentLinks);
    return documentLinks;
  });

  const setDefaultLinkGroup = (documentLinks = []) => {
    if (documentLinks?.find((dl) => !dl.link_group)) {
      setSelectedLinkGroup(defaultLinkGroup);
      return;
    }

    setSelectedLinkGroup(
      documentLinks?.findLast((dl) => !!dl.link_group)?.link_group ||
        defaultLinkGroup
    );
  };

  const loadLinkGroupOptions = useExceptionHandler(async () => {
    let validLinkGroupOptions = await getFileLinkGroupOptions(documentLinkType);
    if (allowedLinkGroups.length > 0) {
      validLinkGroupOptions = validLinkGroupOptions.filter((lg) =>
        allowedLinkGroups.includes(lg.value)
      );
    }

    const uniqueLinkGroups = validLinkGroupOptions.reduce((acc, current) => {
      const x = acc.find((item) => item.value === current.value);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    setLinkGroupOptions([
      { value: defaultLinkGroup, label: defaultLinkGroup },
      ...uniqueLinkGroups,
    ]);
  });

  const formErrors = [
    ...new Set(
      documentLinks
        .map((doc) => {
          if (!doc.link_name) {
            return 'File name is required.';
          }

          if (
            linkGroupsRequireExpiration.includes(doc.link_group) &&
            !doc.expiration_date
          ) {
            return `File "${doc.link_name || ''}" needs an expiration date.`;
          }

          return false;
        })
        .filter((e) => !!e)
    ),
  ];

  const handleClose = () => {
    setShow(false);
    setDocumentLinkSaving(false);
    setDocumentLinks([]);
    setFileIdBeingEdited(null);
  };

  const changeDocumentLinkProperty = ({
    setFileProperty,
    file,
    getFileIdentifierOverride,
  }) => {
    if (!setFileProperty) throw Error('setFileProperty() is required');

    const getFileId = getFileIdentifierOverride || getFileIdentifier;

    setDocumentLinks((prev) => {
      const updatedDocumentLinks = prev.map((prevFile) => {
        if (getFileId(prevFile) === getFileId(file)) {
          return { ...setFileProperty(prevFile), updated: true };
        }
        return prevFile;
      });

      return updatedDocumentLinks;
    });
  };

  const saveDocumentLinks = useExceptionHandler(async (documentLinks) => {
    //Filter out just new document links
    const newDocumentLinks = documentLinks.filter((doc) => doc.file);

    //Filter out just updated documents links that are not new files
    const updatedDocumentLinks = documentLinks.filter(
      (doc) => doc.updated && !doc.file
    );

    setDocumentLinkSaving(true);
    await addDocumentLinks({ newDocumentLinks, updatedDocumentLinks });
    showMessage({ message: 'Document uploaded successfully' });
    await loadDocumentLinks();
    await loadLinkGroupOptions();
    setDocumentLinkSaving(false);
  });

  const generateNewLink = ({ data, file }) => ({
    file: file,
    link_path: data?.fullPath,
    link_name: data?.name,
    row_status_flag: documentLinkStatusValues.Active,
    row: documentLinks.length + 1, //Add a new row
    link_group: selectedLinkGroup === defaultLinkGroup ? '' : selectedLinkGroup,
    updated: true,
  });

  const handleFileChange = useExceptionHandler(
    async (e) => {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('file', file);
      if (entityType) formData.append('entityType', entityType);
      if (entityId != null) formData.append('entityId', String(entityId));

      setDocumentLinkSaving(true);

      const { data } = await FileAPI.addDocumentLinksFile(formData);

      const newLink = generateNewLink({ data, file });

      if (newFileFields) {
        setDocumentLinks((prev) => [...prev, { ...newLink, ...newFileFields }]);
      } else {
        await saveDocumentLinks([...documentLinks, newLink]);
      }

      e.target.value = null;
    },
    () => setDocumentLinkSaving(false)
  );

  const getDocumentLinkFile = useExceptionHandler(
    async ({ file, downloadDocument = false }) => {
      setDocumentLinkSaving(true);
      const blob = await viewDocumentLinksFile({ rowIndex: file.row, file });

      const link = document.createElement('a');
      link.target = '_blank';
      link.href = URL.createObjectURL(blob);
      if (downloadDocument) {
        // Remove the extension from link_name
        const nameWithoutExt = file.link_name.replace(/\.[^/.]+$/, '');
        link.download = nameWithoutExt;
      }
      link.click();
    },
    () => setDocumentLinkSaving(false)
  );

  //Should only work on links with a file
  const deleteUploadedFile = useExceptionHandler(async ({ fileName }) => {
    await FileAPI.deleteDocumentLinksFile(fileName);
    showMessage({ message: 'File deleted successfully' });
    const filteredDocumentLinks = documentLinks.filter(
      (file) => file.file?.name !== fileName
    );
    setDocumentLinks(filteredDocumentLinks);
  });

  const hasPermissionToModifyLinkGroup = (linkGroup) =>
    allowedLinkGroups?.length === 0 || allowedLinkGroups.includes(linkGroup);

  const isFileLocked = (file) => {
    if (!hasPermissionToModifyLinkGroup(file.link_group)) {
      return true;
    }

    if (
      documentLinkType === documentLinkTypes.order &&
      file.key1_cd !== 'order_no'
    ) {
      return true;
    }
    if (
      documentLinkType === documentLinkTypes.customer &&
      file.key2_cd !== 'customer_id'
    ) {
      return true;
    }

    return false;
  };

  const visibleDocumentLinks = documentLinks
    .filter(
      (dl) =>
        dl.row_status_flag !== documentLinkStatusValues.Delete &&
        (dl.link_group === selectedLinkGroup ||
          (!dl.link_group && selectedLinkGroup === defaultLinkGroup))
    )
    .sort((a, b) => b.row - a.row);

  const createNewLinkGroup = (newLinkGroup, onNewGroupCreated) => {
    const linkGroupAlreadyExists = linkGroupOptions.find(
      (o) => o.value?.toLowerCase() === newLinkGroup.toLowerCase()
    );

    if (linkGroupAlreadyExists) {
      onNewGroupCreated();
      return;
    }

    openDialog({
      message: `Move file to a new folder "${newLinkGroup}"?`,
      handleConfirm: () => {
        setLinkGroupOptions((prev) => [
          ...prev,
          {
            value: newLinkGroup,
            label: newLinkGroup,
          },
        ]);

        setSelectedLinkGroup(newLinkGroup);
        onNewGroupCreated();
      },
    });
  };

  return (
    <Modal
      open={show}
      setOpen={(open) => {
        if (!open) {
          if (updatedDocumentLinks.length) {
            openDialog({
              title: 'Unsaved changes',
              message:
                'You have unsaved changes, are you sure you want to close?',
              handleConfirm: handleClose,
            });
          } else {
            handleClose();
          }
        }
      }}
      maxWidth='xl'
      title={defaultTitle || 'Document Links'}
      // Allow backdrop click to close; the setOpen handler above still guards unsaved changes.
      allowBackdropClose
      titleActions={
        hasPermissionToModifyLinkGroup(selectedLinkGroup) ? (
          <Button
            variant='contained'
            component='label'
            color='primary'
            disabled={documentLinkSaving}
          >
            <AddOutlined />
            &nbsp;Upload a file
            {selectedLinkGroup === defaultLinkGroup ? (
              false
            ) : (
              <>
                &nbsp;to&nbsp;
                <strong>{selectedLinkGroup}</strong>
              </>
            )}
            <input type='file' hidden onChange={handleFileChange} />
          </Button>
        ) : (
          false
        )
      }
      actions={
        <Stack
          direction='row'
          justifyContent={'space-between'}
          sx={{ width: '100%', padding: '16px' }}
        >
          {formErrors?.length ? (
            <Typography color='error' variant='caption'>
              <strong>Please fix the following errors:</strong>
              <br />
              {formErrors.map((e, i) => (
                <span key={i}>
                  {i + 1}. {e}
                  <br />
                </span>
              ))}
            </Typography>
          ) : (
            <Box />
          )}
          <LoadingButton
            variant='outlined'
            loading={documentLinkSaving}
            disabled={!updatedDocumentLinks.length || !!formErrors?.length}
            onClick={async () => {
              await saveDocumentLinks(documentLinks);
              handleClose();
            }}
          >
            Save and Close
          </LoadingButton>
        </Stack>
      }
    >
      <GridContainer alignItems='flex-start'>
        <GridItem xs={2.5}>
          <Stack spacing={1}>
            {uniqueGroups
              .map((linkGroup) => {
                const fileCount = documentLinks.reduce((count, doc) => {
                  if (doc.row_status_flag === documentLinkStatusValues.Delete)
                    return count;

                  const isGroupMatch = doc.link_group === linkGroup;
                  const noGroupSelected =
                    !doc.link_group && linkGroup === defaultLinkGroup;

                  return isGroupMatch || noGroupSelected ? count + 1 : count;
                }, 0);

                return { linkGroup, fileCount };
              })
              .filter(
                (g) => g.linkGroup === defaultLinkGroup || g.fileCount > 0
              )
              .map((x) => {
                const { linkGroup, fileCount } = x;
                const isGroupSelected = linkGroup === selectedLinkGroup;

                return (
                  <PaddedCard
                    key={linkGroup}
                    sx={{
                      backgroundColor: isGroupSelected
                        ? colors.darkGray
                        : undefined,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedLinkGroup(linkGroup)}
                  >
                    <Stack direction='row' spacing={2}>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: isGroupSelected ? 'white' : undefined,
                        }}
                      >
                        {linkGroup}
                      </Typography>
                      <Chip size='small' label={fileCount} />
                    </Stack>
                  </PaddedCard>
                );
              })}
          </Stack>
        </GridItem>
        <GridItem xs={9.5}>
          {documentLinks.length > 0 && (
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <CompactTableRow>
                  <CompactTableCell>Name</CompactTableCell>
                  {!hideAreas ? (
                    <CompactTableCell>Area</CompactTableCell>
                  ) : (
                    false
                  )}
                  <CompactTableCell>Uploaded By</CompactTableCell>
                  {customColumns.map((c) => (
                    <CompactTableCell key={c.name}>{c.name}</CompactTableCell>
                  ))}
                  <CompactTableCell>Actions</CompactTableCell>
                </CompactTableRow>
              </TableHead>
              <TableBody>
                {visibleDocumentLinks.map((file) => {
                  const expirationDate = file.expiration_date
                    ? fromUtcDateStringToDate(file.expiration_date)
                    : '';
                  const isExpired =
                    expirationDate && expirationDate < new Date();
                  const cellStyle = isExpired
                    ? { backgroundColor: colors.warningLow }
                    : {};

                  const fileKey = file.document_link_uid || file.link_name;

                  return (
                    <TableRow key={fileKey}>
                      <CompactTableCell sx={{ width: '35%', ...cellStyle }}>
                        {fileIdBeingEdited === fileKey ? (
                          <LabeledInput
                            error={!file.link_name}
                            value={file.link_name}
                            disabled={documentLinkSaving}
                            onChange={(event) => {
                              changeDocumentLinkProperty({
                                file,
                                setFileProperty: (prev) => ({
                                  ...prev,
                                  link_name: event.target.value,
                                }),
                              });
                            }}
                          />
                        ) : (
                          <Link
                            title='Click to download file'
                            onClick={() => {
                              getDocumentLinkFile({ file });
                            }}
                            disabled={documentLinkSaving || !!file.file?.name}
                          >
                            {file.link_name}
                          </Link>
                        )}
                      </CompactTableCell>
                      {!hideAreas ? (
                        <CompactTableCell sx={cellStyle}>
                          <Typography variant='body2'>
                            {file.source_area}
                            {file.is_error_log ? (
                              <span
                                style={{
                                  backgroundColor: colors.warningHigh,
                                  fontSize: '12px',
                                  borderRadius: '4px',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  padding: '2px 4px',
                                  margin: '0 4px',
                                }}
                              >
                                SO Error
                              </span>
                            ) : (
                              false
                            )}
                          </Typography>
                          {file.display_areas?.length > 0 ? (
                            <Typography variant='caption'>
                              {joinString(
                                file.display_areas.filter(
                                  (da) => da !== file.source_area
                                )
                              )}
                            </Typography>
                          ) : (
                            false
                          )}
                        </CompactTableCell>
                      ) : (
                        false
                      )}
                      <CompactTableCell sx={cellStyle}>
                        <Typography variant='caption'>
                          {file.created_by}
                          <br />
                          {formatDateTime(
                            file.date_created,
                            dateFormats.shortWithTimeNoWeekday
                          )}
                        </Typography>
                      </CompactTableCell>
                      {customColumns.map((c) => (
                        <CompactTableCell key={c.name}>
                          {c.renderCell({
                            file,
                            changeDocumentLinkProperty,
                            disabled: documentLinkSaving || isFileLocked(file),
                          })}
                        </CompactTableCell>
                      ))}
                      <CompactTableCell sx={cellStyle}>
                        <Stack
                          direction='row'
                          spacing={1}
                          justifyContent={'space-between'}
                        >
                          <IconButton
                            title='Edit file name'
                            color='primary'
                            onClick={() => {
                              if (isFileLocked(file)) return;
                              setFileIdBeingEdited(fileKey);
                            }}
                            disabled={
                              fileIdBeingEdited === fileKey ||
                              documentLinkSaving ||
                              isFileLocked(file)
                            }
                          >
                            <DriveFileRenameOutline />
                          </IconButton>
                          {showExpirationDate
                            ? (() => {
                                return (
                                  <LabeledInput
                                    type='date'
                                    label={'Expiration'}
                                    textSx={{
                                      width: '130px',
                                    }}
                                    useDateFns
                                    value={expirationDate}
                                    error={!expirationDate}
                                    disabled={
                                      documentLinkSaving || isFileLocked(file)
                                    }
                                    onChange={(date) => {
                                      const newDate = date
                                        ? addHours(startOfDay(date), 12)
                                        : date;

                                      changeDocumentLinkProperty({
                                        file,
                                        setFileProperty: (prev) => ({
                                          ...prev,
                                          expiration_date:
                                            newDate?.toISOString() || '',
                                        }),
                                      });
                                    }}
                                  />
                                );
                              })()
                            : false}
                          <LabeledInput
                            type='select-search'
                            options={linkGroupOptions}
                            value={file.link_group}
                            label='Move to folder'
                            placeholder='Find or create a folder...'
                            freeSolo={canAddNewLinkGroup}
                            disabled={documentLinkSaving || isFileLocked(file)}
                            sx={{ minWidth: '200px' }}
                            onBlur={(e) => {
                              const selectedLinkGroup = e.target.value?.trim();

                              if (!selectedLinkGroup) return;

                              const onNewGroupCreated = () => {
                                if (
                                  allowedLinkGroups.length > 0 &&
                                  !allowedLinkGroups.includes(selectedLinkGroup)
                                )
                                  return;

                                changeDocumentLinkProperty({
                                  file,
                                  setFileProperty: (prev) => ({
                                    ...prev,
                                    link_group:
                                      selectedLinkGroup === defaultLinkGroup
                                        ? ''
                                        : selectedLinkGroup,
                                  }),
                                });
                              };

                              createNewLinkGroup(
                                selectedLinkGroup,
                                onNewGroupCreated
                              );
                            }}
                          />
                          <IconButton
                            icon='download'
                            title='Download File'
                            onClick={() =>
                              getDocumentLinkFile({
                                file,
                                downloadDocument: true,
                              })
                            }
                          >
                            <DownloadSharp />
                          </IconButton>
                          <IconButton
                            title='Delete file'
                            color='error'
                            disabled={documentLinkSaving || isFileLocked(file)}
                            onClick={() => {
                              if (file.file?.name) {
                                openDialog({
                                  message: `Are you sure you want to remove the file ${file.file?.name}?`,
                                  title: 'Delete File',
                                  confirmText: 'Delete',
                                  handleConfirm: () => {
                                    deleteUploadedFile({
                                      fileName: file.file?.name,
                                    });
                                  },
                                });
                                return;
                              }

                              changeDocumentLinkProperty({
                                file,
                                setFileProperty: (prev) => ({
                                  ...prev,
                                  row_status_flag:
                                    documentLinkStatusValues.Delete,
                                }),
                              });
                            }}
                          >
                            <DeleteForever />
                          </IconButton>
                        </Stack>
                      </CompactTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </GridItem>
      </GridContainer>
    </Modal>
  );
}

export default DocumentLinksDialog;

export const documentLinkTypes = {
  customer: 'Customer Maintenance',
  order: 'Order Entry/RMA',
  item: 'Item Maintenance',
  vendor: 'Vendor Maintenance',
  supplier: 'Supplier Maintenance',
};

export const documentLinkStatusValues = {
  Active: 'Active',
  Delete: 'Delete',
  Inactive: 'Inactive',
};

export const sourceAreaCodes = {
  vendor: 1337,
  supplier: 1333,
};

export const folderNames = {
  vendorQuotes: 'Vendor Quotes',
};
